import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    IconButton,
    Box,
    Typography,
    CircularProgress,
    Tooltip
} from '@mui/material';
import { Delete, Refresh, OpenInNew } from '@mui/icons-material';
import { useAppDispatch } from 'src/hooks/redux';
import { setCurrentProject } from 'src/store/slices/projectSlice';
import { addNotification } from 'src/store/slices/uiSlice';

type Props = {
    open: boolean;
    onClose: () => void;
};

const ProjectListModal: React.FC<Props> = ({ open, onClose }) => {
    const dispatch = useAppDispatch();
    const [projects, setProjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState<any | null>(null);

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const { API_PREFIX } = await import('../../config/api');
            const res = await fetch(`${API_PREFIX}/api/projects`);
            if (!res.ok) throw new Error('Failed to fetch projects');
            const data = await res.json();
            setProjects(Array.isArray(data) ? data : []);
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Projects', message: err?.message || String(err) }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleOpen = (p: any) => {
        // normalize dates
        if (p.created) p.created = new Date(p.created);
        if (p.modified) p.modified = new Date(p.modified);
        dispatch(setCurrentProject(p));
        dispatch(addNotification({ type: 'success', title: 'Project Opened', message: `Opened ${p.name || p.id}` }));
        onClose();
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete project? This cannot be undone.')) return;
        try {
            const { API_PREFIX } = await import('../../config/api');
            const res = await fetch(`${API_PREFIX}/api/projects/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete');
            setProjects(prev => prev.filter(p => p.id !== id));
            dispatch(addNotification({ type: 'success', title: 'Deleted', message: 'Project deleted.' }));
            if (preview && preview.id === id) setPreview(null);
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Delete Failed', message: err?.message || String(err) }));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Open Project</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1">Saved Projects</Typography>
                            <Tooltip title="Refresh"><IconButton size="small" onClick={fetchProjects}><Refresh fontSize="small" /></IconButton></Tooltip>
                        </Box>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
                        ) : (
                            <List dense>
                                {projects.map((p: any) => (
                                    <ListItem key={p.id} disablePadding>
                                        <ListItemButton onClick={() => setPreview(p)}>
                                            <ListItemText primary={p.name || p.id} secondary={p.description || (p.type ? String(p.type) : '')} />
                                        </ListItemButton>
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1, mr: 0.5 }}>
                                            <IconButton aria-label="open" onClick={() => handleOpen(p)} size="small"><OpenInNew fontSize="small" /></IconButton>
                                            <IconButton aria-label="delete" onClick={() => handleDelete(p.id)} size="small"><Delete fontSize="small" /></IconButton>
                                        </Box>
                                    </ListItem>
                                ))}
                                {projects.length === 0 && !loading && (
                                    <ListItem><ListItemText primary="No saved projects" /></ListItem>
                                )}
                            </List>
                        )}
                    </Box>
                    <Box sx={{ width: 380 }}>
                        <Typography variant="subtitle1">Preview</Typography>
                        {preview ? (
                            <Box sx={{ mt: 1, bgcolor: 'background.paper', p: 1, borderRadius: 1, maxHeight: 420, overflow: 'auto' }}>
                                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, m: 0 }}>{JSON.stringify(preview, null, 2)}</Box>
                            </Box>
                        ) : (
                            <Typography color="textSecondary">Select a project to preview</Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProjectListModal;
